import React from "react";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import AccessoryPopover from "./AccessoryPopover";
import ValidationError from "./ValidationError";
import { getValidationError, groupingSelected } from "./validations";
import { getSelectedOptionAndSendGtmEvent } from "~/userAnalytics";

export interface AccessorySelections {
  [index: string]: number;
}

interface Props {
  invalidAccessories: readonly string[];
  accessorySelections: AccessorySelections;
  setAccessorySelections: React.Dispatch<
    React.SetStateAction<AccessorySelections>
  >;
  product: PricingProduct;
  handleAddToCartError: (validationError: ValidationError | undefined) => void;
  handleSetErrorMessage: (errorMessage: string) => void;
}

const Accessories = ({
  invalidAccessories,
  accessorySelections,
  setAccessorySelections,
  product,
  handleAddToCartError,
  handleSetErrorMessage,
}: Props) => {
  const { accessories } = product;

  const handleAccessoryChange = (value: string, accessoryName: string) => {
    setAccessorySelections({
      ...accessorySelections,
      [accessoryName]: Number(value),
    });
  };

  const sendGtmEventOnChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    getSelectedOptionAndSendGtmEvent(
      event.target.value,
      "PDP Accessory Click",
      event.target,
    );
  };

  const handleAccessoryDropdownClick = (event: React.SyntheticEvent) => {
    const validationError = getValidationError({
      product,
      quantity: 1,
      validationsToRun: [groupingSelected],
    });

    if (validationError && validationError.message?.length > 0) {
      handleSetErrorMessage(validationError.message);
      handleAddToCartError(validationError);
      event.preventDefault();
    }
  };

  return (
    <fieldset
      className="border-none p-0 m-0 rounded-none"
      aria-required
      tabIndex={-1}
    >
      <legend className="accessoriesSideHeading text-xs">
        <strong>Accessories & Options</strong>
      </legend>
      {accessories.map((accessory, index) => (
        <div
          className="configure-contain form"
          key={accessory.name}
          data-testid={`accessory-${index + 1}`}
        >
          <div className="form__group">
            <div
              className={`form__control-container${
                !accessorySelections[accessory.name] ? " no-selection" : ""
              }${
                accessory.extendedInfo
                  ? " form__control-container--popover"
                  : ""
              }${accessory.required ? " form__control--required" : ""}${
                invalidAccessories.includes(accessory.name) ? " error" : ""
              }`}
            >
              <select
                className="form__control text-ellipsis"
                aria-describedby={
                  invalidAccessories.includes(accessory.name) &&
                  !accessorySelections[accessory.name]
                    ? "errorMessageId"
                    : accessory.name
                }
                name="accessories"
                defaultValue=""
                data-testid={`accessory-dropdown-${index + 1}`}
                onChange={(e) => {
                  handleAccessoryChange(e.target.value, accessory.name);
                  sendGtmEventOnChange(e);
                }}
                onMouseDown={handleAccessoryDropdownClick}
              >
                <option value="" disabled>
                  {accessory.name}
                </option>
                {!accessory.required && <option value="0">No selection</option>}
                {accessory.options.map((option) => (
                  <option
                    value={option.id}
                    disabled={!option.available}
                    key={option.id}
                  >
                    {`${
                      option.description
                    } - ${formatAsCurrency.formatIfUndefined(option.price)}${
                      !option.available ? " [OUT OF STOCK]" : ""
                    }`}
                  </option>
                ))}
              </select>
              {accessory.extendedInfo && (
                <span className="form__popover">
                  <AccessoryPopover
                    accessoryName={accessory.name}
                    extendedInfo={accessory.extendedInfo}
                  />
                </span>
              )}
              <label className="form__label h-4" id={accessory.name}>
                <span className="form__label-text">{accessory.name}</span>
                {accessory.required && (
                  <span className="required">Required</span>
                )}
              </label>
              <i className="icon-down-micro form__control-icon form__control-icon--lg" />
            </div>
          </div>
        </div>
      ))}
    </fieldset>
  );
};

export default Accessories;
